import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsFocused } from "@react-navigation/native";
import dayjs from "dayjs";
import { getHandoverVideoContext } from "@services/api";

const INITIAL = {
  loading: true,
  enabled: false,
  bypass_allowed: false,
  reached: false,
  routes: [],
  crates: [],
};

const ymd = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : null);

/**
 * Crate handover-video state for a CP's deliverable crates.
 *
 * The backend context returns EVERY open-route crate, each carrying its route
 * `delivery_date`. Pass `date` to scope to a single delivery day: the strip on
 * each My-Delivery tab/date-card is date-scoped so its count reflects only that
 * date's crates (and hides when that date has none). Without `date`, returns the
 * full set (used by the checklist when opened un-scoped).
 *
 * crates: [{ crate_id, flow_type, delivery_date, num_items, found_item_qty,
 *   video_status, ... }]
 * video_status: 'PENDING' | 'INITIATED' | 'UPLOADED' | 'BYPASSED' | 'FAILED'
 * found_item_qty: per-flow config { CRATE_HANDOVER: {enabled, mandatory}, ... }
 * is passed through from the context payload for the found-item-qty input.
 */
const useHandoverVideos = (date) => {
  const isFocused = useIsFocused();
  const [state, setState] = useState(INITIAL);

  const refetch = useCallback(async () => {
    try {
      const { data } = await getHandoverVideoContext();
      setState({ ...INITIAL, ...data, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (isFocused) {
      refetch();
    }
  }, [isFocused, refetch]);

  const scopeDate = date ? ymd(date) : null;

  // Scope by display_date — the EDD-based date the tabs group cards under.
  // A missed crate's EDD rolls forward, so its card (and strip) move to the
  // new date while recording/the gate stay keyed on the route delivery_date.
  const crates = useMemo(() => {
    if (!scopeDate) return state.crates;
    return state.crates.filter(
      (c) => ymd(c.display_date || c.delivery_date) === scopeDate,
    );
  }, [state.crates, scopeDate]);

  // Reached unlocks recording. When scoped, reflect the routes the scoped
  // crates are actually on (their delivery_date), not the card's display date.
  const reached = useMemo(() => {
    if (!scopeDate) return state.reached;
    const routeDates = new Set(crates.map((c) => ymd(c.delivery_date)));
    return state.routes.some(
      (r) => routeDates.has(ymd(r.delivery_date)) && r.reached_at,
    );
  }, [state.reached, state.routes, crates, scopeDate]);

  const counts = useMemo(() => {
    const total = crates.length;
    const done = crates.filter(
      (c) => c.video_status === "UPLOADED" || c.video_status === "BYPASSED",
    ).length;
    return { total, done, pending: total - done };
  }, [crates]);

  return { ...state, crates, reached, counts, refetch };
};

export default useHandoverVideos;
